import { computed, useState } from "#imports";

const LOCAL_LOGIN_RESPONSE_KEY = "login_response";

type LoginResponse = {
  Менеджер?: string;
  ТелефонМенеджера?: string;
  EMailМенеджера?: string;
  ФотоМенеджера?: string;
  АссистентМенеджера?: string;
  ТелефонАссистентаМенеджера?: string;
  EMailАссистентаМенеджера?: string;
  ФотоАссистентаМенеджера?: string;
  ПоказыватьРазделТовары?: unknown;
  ПоказыватьРазделКорзина?: unknown;
  ПоказыватьРазделЗаказы?: unknown;
  ПоказыватьРазделОтгрузки?: unknown;
  ПоказыватьРазделФинансы?: unknown;
  ПоказыватьРазделAPI?: unknown;
} | null;

const normalizePath = (path: string) => {
  if (path === "/") return "/";
  return path.replace(/\/+$/, "") || "/";
};

const normalizeFlag = (value: unknown) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

const parseStoredLoginResponse = (raw: string | null): LoginResponse => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);

    if (typeof parsed === "string") {
      return JSON.parse(parsed) as LoginResponse;
    }

    return parsed && typeof parsed === "object" ? (parsed as LoginResponse) : null;
  } catch {
    return null;
  }
};

const parseIncomingLoginResponse = (payload: unknown): LoginResponse => {
  if (!payload) return null;

  if (typeof payload === "string") {
    return parseStoredLoginResponse(payload);
  }

  return typeof payload === "object" ? (payload as LoginResponse) : null;
};

export const useLoginResponse = () => {
  const loginResponse = useState<LoginResponse>("auth:login-response", () => null);

  const hydrateLoginResponse = () => {
    if (!process.client) return;
    loginResponse.value = parseStoredLoginResponse(
      localStorage.getItem(LOCAL_LOGIN_RESPONSE_KEY),
    );
  };

  const persistLoginResponse = (payload: unknown) => {
    const parsed = parseIncomingLoginResponse(payload);
    if (!parsed) return;

    const nextValue = {
      ...(loginResponse.value ?? {}),
      ...parsed,
    } as LoginResponse;

    loginResponse.value = nextValue;

    if (!process.client) return;
    localStorage.setItem(LOCAL_LOGIN_RESPONSE_KEY, JSON.stringify(nextValue));
  };

  const clearLoginResponse = () => {
    loginResponse.value = null;

    if (!process.client) return;
    localStorage.removeItem(LOCAL_LOGIN_RESPONSE_KEY);
  };

  const canViewGoods = computed(() =>
    normalizeFlag(loginResponse.value?.ПоказыватьРазделТовары),
  );
  const canViewCart = computed(() =>
    normalizeFlag(loginResponse.value?.ПоказыватьРазделКорзина),
  );
  const canViewOrders = computed(() =>
    normalizeFlag(loginResponse.value?.ПоказыватьРазделЗаказы),
  );
  const canViewDelivery = computed(() =>
    normalizeFlag(loginResponse.value?.ПоказыватьРазделОтгрузки),
  );
  const canViewFinance = computed(() =>
    normalizeFlag(loginResponse.value?.ПоказыватьРазделФинансы),
  );
  const canViewApi = computed(() =>
    normalizeFlag(loginResponse.value?.ПоказыватьРазделAPI),
  );

  const isRouteAllowed = (path: string) => {
    const normalized = normalizePath(path);

    if (
      ["/search", "/goods", "/favorites", "/compare"].some(
        (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
      )
    ) {
      return canViewGoods.value;
    }

    if (normalized === "/cart" || normalized.startsWith("/cart/")) {
      return canViewCart.value;
    }

    if (normalized === "/orders" || normalized.startsWith("/orders/")) {
      return canViewOrders.value;
    }

    if (
      ["/delivery", "/logist"].some(
        (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
      )
    ) {
      return canViewDelivery.value;
    }

    if (normalized === "/finance" || normalized.startsWith("/finance/")) {
      return canViewFinance.value;
    }

    if (normalized === "/api" || normalized.startsWith("/api/")) {
      return canViewApi.value;
    }

    return true;
  };

  return {
    loginResponse,
    hydrateLoginResponse,
    persistLoginResponse,
    clearLoginResponse,
    canViewGoods,
    canViewCart,
    canViewOrders,
    canViewDelivery,
    canViewFinance,
    canViewApi,
    isRouteAllowed,
  };
};
